import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  CircleUserRound, ChevronRight, Menu, RotateCcw,
  ThumbsUp, ThumbsDown, Download, Copy, Mic, ArrowUp,
} from 'lucide-react'
import AppSidebar from '@/components/AppSidebar'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import { apiService } from '@/service/api'
import type { DigitalCoworkerRecord } from '@/service/api'
import { useDigitalCoworkerChat } from './useDigitalCoworkerChat'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useTranslation } from '@/i18n'
import { coworkerAvatarUrl } from '@/utils/coworkerAvatar'
// RecentChatPopover disabled — no conversation persistence for DC chat yet

const SUGGESTIONS = [
  'How to reduce unit downtime?',
  'Best way to optimize crude yield?',
  'How to troubleshoot pressure drop?',
]

export default function ChatPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [coworker, setCoworker] = useState<DigitalCoworkerRecord | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const { messages, isLoading, input, setInput, sendMessage } = useDigitalCoworkerChat(id || '')

  useEffect(() => {
    if (!id) return
    apiService.listDigitalCoworkers().then((r) => {
      const found = r.digital_coworkers.find((c) => c.id === id)
      if (found) setCoworker(found)
      else { toast.error(t('digital_coworker:chat.coworkerNotFound', { defaultValue: 'Coworker not found' })); navigate('/digital-coworker') }
    }).catch(() => {
      toast.error(t('digital_coworker:chat.loadError', { defaultValue: 'Failed to load coworker' }))
      navigate('/digital-coworker')
    })
  }, [id, navigate, t])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSend = () => {
    if (!input.trim() || isLoading) return
    sendMessage()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const coworkerName = coworker?.name || t('digital_coworker:chat.defaultName', { defaultValue: 'Digital Coworker' })
  const avatarUrl = coworkerAvatarUrl(coworker)
  const getInitials = (name: string) => name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()

  // Get conversation title from first user message
  const firstUserMsg = messages.find((m) => m.role === 'user')
  const conversationTitle = firstUserMsg
    ? (firstUserMsg.parts.find((p) => p.type === 'text') as { text: string } | undefined)?.text?.slice(0, 50) || t('digital_coworker:chat.newChat', { defaultValue: 'New Chat' })
    : t('digital_coworker:chat.newChat', { defaultValue: 'New Chat' })

  return (
    <div className="flex h-screen">
      <AppSidebar />
      <div className="flex flex-1 flex-col min-w-0">
        {/* Topbar — breadcrumb */}
        <header className="flex items-center justify-between border-b border-[#e5e5e5] bg-white px-6 py-4 text-sm dark:border-dark-border dark:bg-dark-card">
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => navigate('/digital-coworker')} className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors">
              <CircleUserRound className="size-4" />
              {t('digital_coworker:chat.breadcrumbRoot', { defaultValue: 'Digital Coworker' })}
            </button>
            <ChevronRight className="size-4 text-muted-foreground" />
            <button type="button" onClick={() => navigate('/digital-coworker')} className="text-muted-foreground hover:text-foreground transition-colors">
              {coworkerName}
            </button>
            <ChevronRight className="size-4 text-muted-foreground" />
            <span className="font-medium text-foreground">{t('digital_coworker:chat.breadcrumbChat', { defaultValue: 'Chat' })}</span>
          </div>
        </header>

        {/* Sub-topbar — conversation title */}
        <div className="flex items-center gap-2 border-b border-[#e5e5e5] bg-white px-6 py-3 dark:border-dark-border dark:bg-dark-card">
          <button type="button" disabled className="p-1 opacity-50 cursor-not-allowed">
            <Menu className="size-5" />
          </button>
          <span className="text-sm font-medium truncate">{conversationTitle}</span>
        </div>

        {/* Chat area */}
        <div className="flex-1 overflow-y-auto bg-white px-6 py-6 dark:bg-dark-card">
          <div className="space-y-6">
            {messages.filter((m) => m.role !== 'system').map((msg) => {
              const textPart = msg.parts.find((p) => p.type === 'text') as { text: string } | undefined
              const text = textPart?.text || ''

              if (msg.role === 'user') {
                return (
                  <div key={msg.id} className="flex justify-end">
                    <div className="max-w-[70%]">
                      <div className="rounded-lg bg-gray-900 px-4 py-3 text-sm text-white dark:bg-gray-800">
                        {text}
                      </div>
                      <div className="mt-1 flex items-center justify-end gap-2 text-xs text-muted-foreground">
                        <span>{msg.createdAt?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        <button type="button" className="hover:text-foreground"><Copy className="size-3.5" /></button>
                      </div>
                    </div>
                  </div>
                )
              }

              return (
                <div key={msg.id}>
                  {/* Avatar + name */}
                  <div className="flex items-center gap-2 mb-2">
                    <div className="size-6 rounded-full bg-orange-100 flex items-center justify-center text-xs font-semibold text-orange-600 overflow-hidden">
                      {avatarUrl ? (
                        <img src={avatarUrl} alt={coworkerName} className="size-full object-cover" />
                      ) : getInitials(coworkerName)}
                    </div>
                    <span className="text-sm font-medium">{coworkerName}</span>
                  </div>
                  {/* Message content */}
                  <div className="prose prose-sm max-w-none text-sm text-foreground dark:prose-invert">
                    {msg.status === 'streaming' && !text ? (
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <span className="animate-pulse">●</span>
                        <span className="animate-pulse animation-delay-200">●</span>
                        <span className="animate-pulse animation-delay-400">●</span>
                      </div>
                    ) : (
                      <div dangerouslySetInnerHTML={{ __html: text.replace(/\n/g, '<br/>').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/• /g, '<li>').replace(/<li>/g, '</li><li>') }} />
                    )}
                  </div>
                  {/* Message toolbar */}
                  {msg.status === 'ready' && (
                    <div className="mt-3 flex items-center gap-1">
                      <IntuiButton variant="outline" size="icon-lg" onClick={() => sendMessage(text)}>
                        <RotateCcw className="size-4" />
                      </IntuiButton>
                      <IntuiButton variant="outline" size="icon-lg">
                        <ThumbsUp className="size-4" />
                      </IntuiButton>
                      <IntuiButton variant="outline" size="icon-lg">
                        <ThumbsDown className="size-4" />
                      </IntuiButton>
                      <IntuiButton variant="outline" size="icon-lg">
                        <Download className="size-4" />
                      </IntuiButton>
                      <IntuiButton variant="outline" size="icon-lg" onClick={() => { navigator.clipboard.writeText(text); toast.success(t('digital_coworker:chat.copied', { defaultValue: 'Copied' })) }}>
                        <Copy className="size-4" />
                      </IntuiButton>
                    </div>
                  )}
                </div>
              )
            })}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Bottom — suggestions + input */}
        <div className="bg-white px-6 py-4 dark:bg-dark-card">
          <div className="mx-auto max-w-[800px]">
            {/* Suggestion chips */}
            {messages.length <= 2 && (
              <div className="flex items-center justify-center gap-3 mb-4">
                {SUGGESTIONS.map((s) => (
                  <IntuiButton key={s} variant="outline" size="sm" onClick={() => sendMessage(s)}>
                    {s}
                  </IntuiButton>
                ))}
              </div>
            )}

            {/* Input card */}
            <div className="rounded-xl border border-[#e5e5e5] bg-white shadow-sm dark:border-dark-border dark:bg-dark-card">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('digital_coworker:chat.askPlaceholder', { name: coworkerName, defaultValue: 'Ask {{name}} anything...' })}
                disabled={isLoading}
                rows={2}
                className="w-full resize-none bg-transparent px-4 pt-4 pb-2 text-sm outline-none placeholder:text-muted-foreground"
              />
              <div className="flex items-center justify-end gap-2 px-4 pb-3">
                <button type="button" disabled className="size-5 text-muted-foreground opacity-50">
                  <Mic className="size-5" />
                </button>
                <span className="rounded-full border border-[#e5e5e5] px-3 py-1 text-xs font-medium text-foreground">
                  {t('digital_coworker:chat.board', { defaultValue: 'Board' })}
                </span>
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={!input.trim() || isLoading}
                  className={cn(
                    'flex size-8 items-center justify-center rounded-full transition-colors',
                    input.trim() && !isLoading
                      ? 'bg-[#0e7490] text-white hover:bg-[#0e7490]/90'
                      : 'bg-gray-200 text-gray-400 dark:bg-gray-700'
                  )}
                >
                  <ArrowUp className="size-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
